import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { urlFor } from '../../client'
import { announceAnalyticsInteraction } from '../../lib/analytics'
import { listingAnalyticsAttributes } from '../../lib/analytics-content.mjs'
import {
  GallerySection, GalleryHeading, GalleryHint, PhotoGrid, PhotoFigure, PhotoCaption, PhotoButton,
  PhotoDialog, DialogHeader, DialogImage, DialogControls, GalleryControl,
} from './venue-gallery.styles'

const assetId = (photo) => photo?.asset?._id || photo?.asset?._ref || photo?._key
const photoSource = (photo, width) => photo.src || urlFor(photo).width(width).fit('max').auto('format').url()

export default function VenueGallery({ images = [], mainImage, venueName = 'Venue', listing = {} }) {
  const headingId = useId()
  const dialogRef = useRef(null)
  const movedRef = useRef(false)
  const [activeIndex, setActiveIndex] = useState(null)
  const photos = useMemo(() => {
    const seen = new Set([assetId(mainImage)])
    return (Array.isArray(images) ? images : []).filter((photo) => {
      const id = assetId(photo)
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })
  }, [images, mainImage])
  const isOpen = activeIndex !== null
  const activePhoto = photos[activeIndex]
  const galleryId = `${listing._id || 'venue'}-photos`
  const attrs = listingAnalyticsAttributes(listing)
  const analyticsContext = {
    module_id: galleryId, module_type: 'photo_gallery', placement_id: 'venue-gallery',
    listing_id: attrs['data-analytics-listing-id'], business_id: attrs['data-analytics-business-id'],
    business_name: listing.location || venueName, business_category: listing.category,
  }
  const announcePhoto = (action, index) => {
    const photo = photos[index]
    if (!photo) return
    announceAnalyticsInteraction('cta_selected', {
      ...analyticsContext, cta_id: `${galleryId}-${action}`, cta_type: 'photo_viewer',
      cta_label: action === 'open' ? 'View photo' : 'Close photo viewer',
      photo_id: photo._key || assetId(photo), photo_position: index + 1, photo_count: photos.length,
      interaction_type: 'gallery',
    })
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !isOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialog.showModal()
    return () => {
      dialog.close()
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  if (!photos.length) return null

  const openPhoto = (index) => {
    movedRef.current = false
    announcePhoto('open', index)
    setActiveIndex(index)
  }
  const movePhoto = (step, interactionType = 'click') => {
    if (activeIndex === null) return
    const nextIndex = (activeIndex + step + photos.length) % photos.length
    if (!movedRef.current) {
      movedRef.current = true
      announceAnalyticsInteraction('carousel_used', {
        ...analyticsContext, direction: step < 0 ? 'previous' : 'next', item_position: nextIndex + 1,
        photo_id: photos[nextIndex]._key || assetId(photos[nextIndex]), interaction_type: interactionType,
      })
    }
    setActiveIndex(nextIndex)
  }
  const close = () => dialogRef.current.close()

  return (
    <GallerySection id={galleryId} aria-labelledby={headingId} data-analytics-module-id={galleryId} data-analytics-module-type="photo_gallery" data-analytics-placement-id="venue-gallery">
      <GalleryHeading id={headingId}>Photos</GalleryHeading>
      <GalleryHint>Select a photo to view it larger.</GalleryHint>
      <PhotoGrid>
        {photos.map((photo, index) => (
          <PhotoFigure key={photo._key || assetId(photo)}>
            <PhotoButton
              type="button"
              aria-label={`View photo ${index + 1} of ${photos.length}: ${photo.alt || venueName}`}
              aria-haspopup="dialog"
              onClick={() => openPhoto(index)}
            >
              <Image
                src={photoSource(photo, 1000)}
                alt={photo.alt || `${venueName}, photo ${index + 1}`}
                fill
                sizes="(max-width: 599px) 92vw, (max-width: 899px) 45vw, 380px"
              />
            </PhotoButton>
            {photo.caption && <PhotoCaption>{photo.caption}</PhotoCaption>}
          </PhotoFigure>
        ))}
      </PhotoGrid>
      <PhotoDialog
        ref={dialogRef}
        aria-label={`${venueName} photos`}
        onClose={() => { announcePhoto('close', activeIndex); setActiveIndex(null) }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return
          const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect()
          if (event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom) close()
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault()
            movePhoto(event.key === 'ArrowLeft' ? -1 : 1, 'keyboard')
          } else if (event.key === 'Tab') {
            const buttons = event.currentTarget.querySelectorAll('button:not(:disabled)')
            const first = buttons[0]
            const last = buttons[buttons.length - 1]
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault()
              last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault()
              first.focus()
            }
          }
        }}
      >
        <DialogHeader>
          <span>{venueName}</span>
          <GalleryControl type="button" onClick={close} autoFocus aria-label="Close photo viewer">Close</GalleryControl>
        </DialogHeader>
        {activePhoto && (
          <>
            <DialogImage>
              <Image
                src={photoSource(activePhoto, 2000)}
                alt={activePhoto.alt || `${venueName}, photo ${activeIndex + 1}`}
                fill
                sizes="(max-width: 1100px) 95vw, 1100px"
                placeholder={activePhoto.asset?.metadata?.lqip ? 'blur' : 'empty'}
                blurDataURL={activePhoto.asset?.metadata?.lqip}
              />
            </DialogImage>
            {activePhoto.caption && <PhotoCaption as="p">{activePhoto.caption}</PhotoCaption>}
          </>
        )}
        <DialogControls>
          <GalleryControl type="button" onClick={() => movePhoto(-1)} disabled={photos.length < 2} aria-label="Previous photo">Previous</GalleryControl>
          <span role="status" aria-live="polite">Photo {activeIndex + 1} of {photos.length}</span>
          <GalleryControl type="button" onClick={() => movePhoto(1)} disabled={photos.length < 2} aria-label="Next photo">Next</GalleryControl>
        </DialogControls>
      </PhotoDialog>
    </GallerySection>
  )
}
